import type { Snapshot } from "@app/protocol";
import { useEffect, useRef } from "react";
import type { TurnClock } from "../components/PodParts";
import type { DealRun } from "../components/useDeal";
import { COUNTDOWN_START, countdownStep } from "./countdown";
import { type TableCue, tableCues } from "./cues";
import type { Cue } from "./engine";
import { sounds } from "./sounds";

/** A played card is heard as it lands, not as it leaves (the trick's fly-in is 320 ms). */
const CARD_LAND_MS = 250;

/** Tick, then the recording, on your own clock only; stops when the turn does (decision 62). */
function useCountdown(clock: TurnClock | null): void {
  const latest = useRef(clock);
  latest.current = clock;
  const active = clock !== null;
  useEffect(() => {
    if (!active) return;
    let state = COUNTDOWN_START;
    const check = () => {
      const current = latest.current;
      if (current === null) return;
      const step = countdownStep(state, current.endsAt - Date.now(), current.budget);
      if (step.bed !== null) sounds.bed(step.bed === "start");
      if (step.tick) sounds.play("sure-tik");
      state = step.next;
    };
    check();
    const id = window.setInterval(check, 200);
    return () => {
      window.clearInterval(id);
      if (state.bed) sounds.bed(false);
    };
  }, [active]);
}

/** The recordings that end a hand and a match (decision 65). */
const ENDINGS: Record<Exclude<TableCue, "card">, Cue> = {
  "hand-end": "el-bitti",
  "match-end": "mac-sonu",
};

function playCue(cue: TableCue): void {
  if (cue === "card") sounds.play("kart-at", CARD_LAND_MS);
  else sounds.play(ENDINGS[cue]);
}

/** Cards landing, a hand or the match ending: only what changed live. */
function useSnapshotCues(snapshot: Snapshot): void {
  const previous = useRef(snapshot);
  useEffect(() => {
    const before = previous.current;
    previous.current = snapshot;
    if (before !== snapshot) tableCues(before, snapshot).forEach(playCue);
  }, [snapshot]);
}

/** The shuffle as the deck charges, before the cards fly (decision 63). */
function useDealSound(deal: DealRun | null): void {
  const latest = useRef(deal);
  latest.current = deal;
  const dealId = deal?.id ?? null;
  useEffect(() => {
    const run = latest.current;
    if (dealId === null || run === null) return;
    sounds.play("kart-dagit");
  }, [dealId]);
}

/** "Your turn" once, the moment it becomes yours. */
function useYourTurnSound(ready: boolean): void {
  const wasReady = useRef(ready);
  useEffect(() => {
    if (ready && !wasReady.current) sounds.play("sira-bende");
    wasReady.current = ready;
  }, [ready]);
}

/** Every sound of the table screen (review 0013); see docs/ekranlar/masa.md. */
export function useTableSounds({
  snapshot,
  yourTurn,
  deal,
  clock,
}: {
  snapshot: Snapshot;
  yourTurn: boolean;
  deal: DealRun | null;
  clock: TurnClock | null;
}): void {
  useEffect(() => sounds.preload(), []);
  useSnapshotCues(snapshot);
  useDealSound(deal);
  // Announced with your panel, once the cards have landed; nothing after the match (U6).
  const ready = yourTurn && deal === null && snapshot.table.status !== "finished";
  useYourTurnSound(ready);
  useCountdown(ready ? clock : null);
}
